import { CrossClientQueryDecision, CrossClientQueryLogicalQuestionDecomposition, CrossClientQueryResultAnswer } from '../../../models/CrossClient'

const evaluateAnd = (left: CrossClientQueryDecision, right: CrossClientQueryDecision): CrossClientQueryDecision => {
  if (left === false || right === false) {
    return false
  }
  return left === null || right === null ? null : true
}

const evaluateOr = (left: CrossClientQueryDecision, right: CrossClientQueryDecision): CrossClientQueryDecision => {
  if (left === true || right === true) {
    return true
  }
  return left === null || right === null ? null : false
}

const evaluateNot = (value: CrossClientQueryDecision): CrossClientQueryDecision => {
  return value === null ? null : !value
}

const tokenize = (formula: string): string[] => {
  return formula
    .replace(/\(/g, ' ( ')
    .replace(/\)/g, ' ) ')
    .split(/\s+/)
    .filter((token) => token.length > 0)
}

export const evaluateLogicalFormula = (
  decomposition: CrossClientQueryLogicalQuestionDecomposition,
  answers: CrossClientQueryResultAnswer[],
): CrossClientQueryDecision => {
  const variableValues = new Map<string, CrossClientQueryDecision>()

  decomposition.atomicClauses.forEach((clause, index) => {
    variableValues.set(clause.variable, answers[index].decision)
  })

  const parseExpression = (tokens: string[]): CrossClientQueryDecision => {
    let index = 0

    const parsePrimary = (): CrossClientQueryDecision => {
      const token = tokens[index]

      if (token === '(') {
        index++ // Skip '('
        const result = parseOrExpression()
        index++ // Skip ')'
        return result
      }

      if (token === 'NOT') {
        index++ // Skip 'NOT'
        const operand = parsePrimary()
        return evaluateNot(operand)
      }

      // Variable
      const value = variableValues.get(token)
      index++
      return value !== undefined ? value : null
    }

    const parseAndExpression = (): CrossClientQueryDecision => {
      let left = parsePrimary()

      while (index < tokens.length && tokens[index] === 'AND') {
        index++ // Skip 'AND'
        const right = parsePrimary()
        left = evaluateAnd(left, right)
      }

      return left
    }

    const parseOrExpression = (): CrossClientQueryDecision => {
      let left = parseAndExpression()

      while (index < tokens.length && tokens[index] === 'OR') {
        index++ // Skip 'OR'
        const right = parseAndExpression()
        left = evaluateOr(left, right)
      }

      return left
    }

    return parseOrExpression()
  }

  const tokens = tokenize(decomposition.logicalFormula)
  return parseExpression(tokens)
}
